import { useState } from "react";
import { PnlChartPopupStyles } from "./PnlChartPopupStyles";
import type {
  DataDumpAggregateType,
  PnlUnknownLoss,
} from "../../../../../types/types";
import { animated, useSpring } from "@react-spring/web";
import {
  usePrevious,
  useMount,
  BREAKPOINT_DESKTOP,
  getIsIe11,
  NAV_HEIGHT_PX,
  CHART_MAX_WIDTH,
  BREAKPOINT_DESKTOP_XL,
} from "../../../../../constants/constants";
import { GroupMetric } from "../../../GroupMetrics/GroupMetric";
import {
  calcMetric,
  findDataDumpsTrendChartObjFromStore,
} from "../../../../../utils/metrics";
import { GroupMetricsStyles } from "../../../GroupMetrics/GroupMetricsStyles";
import { useSettings } from "../../../../../contexts/SettingsManager/SettingsManager";
import {
  useGetPageFromPath,
  usePnlPopupTimeRange,
  useTrendChartDataDumps,
  useIsAdmin,
  useFormatZoned,
  useSetPnlPopupTimeRange,
  useTrendsPageFilterState,
  useContainerDimensionsObserver,
  useAllProcesses,
} from "../../../../../utils/customHooks/genericHooks";
import { useCustomerKey } from "../../../../../utils/customHooks/useCustomerKey";
import PnlPopupTitleBar from "./PnlPopupTitleBar";
import { CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING } from "../../../../../constants/customers";
import {
  MAX_DATA_DUMP_TIME_RANGE_MS,
  LOT_PROGRESS_DATA_DUMP_TIME_RANGE_MS,
} from "../../../../../segments/DataDumpGetter/DataDumpGetter";
import { getSupervisorCommentDataTimeData } from "./pnlChartPopupUtils";
import SupervisorContext from "../SupervisorContext/SupervisorContext";
import { useMediaQuery } from "@mui/material";
import {
  useTrendChartBarsArray,
  useProcessIdForSupervisorContext,
  useTrendChartBarRangesForBarstacks,
} from "utils/customHooks/trendChartHooks";
import { PnlSummaryChartContent } from "components/Dashboard/PnlSummaryChart/PnlSummaryChartContent/PnlSummaryChartContent";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { usePnlChartSectionsArr } from "./usePnlPopupChartSectionsArr";
import { useIsDarkMode } from "store/atoms";
import styled from "styled-components/macro";
import { useVisibleMetrics } from "components/Dashboard/GroupMetrics/useVisibleMetrics";
import { getShowTooltip } from "components/Dashboard/GroupMetrics/groupMetricsUtils";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";

type PnlChartPopupContentProps = {
  containerWidth: number;
  isFullPagePopup: boolean;
};

// tslint:disable-next-line
export function PnlChartPopupContent({
  containerWidth,
  isFullPagePopup,
}: PnlChartPopupContentProps) {
  const isStorybook = useIsStorybook();
  const [
    {
      general: { enableLotInfoModule },
    },
  ] = useFlightConfig();
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const { isTrendsPage } = useGetPageFromPath();
  const pnlPopupTimeRange = usePnlPopupTimeRange();
  const setPnlPopupTimeRange = useSetPnlPopupTimeRange();

  const ckey = useCustomerKey();
  const dataDumpsTrendChart = useTrendChartDataDumps();

  const rollupMetricTrendChartBarsArray = useTrendChartBarsArray(isTrendsPage);
  const rollupMetric = rollupMetricTrendChartBarsArray.find(({ range }) => {
    return pnlPopupTimeRange && range.ge === pnlPopupTimeRange.ge;
  });
  const { pnlChartSectionsArr } = usePnlChartSectionsArr();

  const isDesktopOrLarger = useMediaQuery(
    `(min-width: ${BREAKPOINT_DESKTOP}px)`
  );

  const { timeZoneLocaleString, shouldUseCustomTimeZone } = useSettings();

  // hold onto the last values so we can animate the modal closed
  const prevPnlChartSections = usePrevious(pnlChartSectionsArr);
  const prevPnlPopupTimeRange = usePrevious(pnlPopupTimeRange);
  const timeRange = pnlPopupTimeRange || prevPnlPopupTimeRange;

  const { isHubletPage, isGridLayoutPage, isTimelinePage } =
    useGetPageFromPath();

  const isModalOpen = Boolean(pnlPopupTimeRange);

  // ensure modal springs up on enter
  const [isMounted, setIsMounted] = useState(false);
  useMount(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  });

  const springUpOnOpen = useSpring({
    transform: `translateY(${isModalOpen && isMounted ? 0 : 50}px)`,
    opacity: isModalOpen && isMounted ? 1 : 0,
    immediate: isStorybook,
  });

  const dataDumpForPopup =
    timeRange &&
    findDataDumpsTrendChartObjFromStore({
      dataDumpsTrendChart,
      geToFind: timeRange.ge,
    });

  const isTooLongForDataDump =
    timeRange &&
    timeRange.lt - timeRange.ge >
      (enableLotInfoModule
        ? LOT_PROGRESS_DATA_DUMP_TIME_RANGE_MS
        : MAX_DATA_DUMP_TIME_RANGE_MS);

  const visibleMetrics = useVisibleMetrics();
  const metrics = visibleMetrics.PnlPopup;

  const processIdForSupervisorContext = useProcessIdForSupervisorContext();

  const isUserSupervisor = useIsUserSupervisor();

  let unknownPnlLoss = {} as PnlUnknownLoss;

  // If we receive a non-empty processId, that means this customer's process uses supervisor context
  const hasProcessIds = processIdForSupervisorContext !== "";

  // hide the supervisor context for customers that only want to see the Top 5 Downtimes
  const shouldShowSupervisorContext = hasProcessIds && isUserSupervisor;

  if (shouldShowSupervisorContext && timeRange) {
    const dataDumpForFocusRange = dataDumpsTrendChart
      ? findDataDumpsTrendChartObjFromStore({
          dataDumpsTrendChart,
          geToFind: timeRange.ge,
        })
      : ({} as DataDumpAggregateType);

    if (dataDumpForFocusRange?.pnlLosses) {
      unknownPnlLoss = dataDumpForFocusRange?.pnlLosses?.unknown || {};
    }
  }

  const { height: popupElementHeight } = useContainerDimensionsObserver(
    ".pnlChartPopupContent"
  );

  const height = isStorybook ? 1000 : popupElementHeight || 0;
  const isHubletPageTabletOrSmaller = isHubletPage && !isDesktopOrLarger;

  const width = containerWidth;
  const isIe11 = getIsIe11();

  const formatZoned = useFormatZoned();
  const [isDarkMode] = useIsDarkMode();
  const fullPageHeight = "calc(100vh - " + 2 * NAV_HEIGHT_PX + "px)";
  const animating = springUpOnOpen.opacity.get() !== 1; // don't use `height` until the animation is done (bad performance, many re-renders)

  const trendChartBarRangesForBarstacks = useTrendChartBarRangesForBarstacks();
  // * edge case -> trends page, filtered by shift, trend chart bin size = weeks => need to display the full week range in the title, while timeRange uses shift range to apply proper filtering to the data (in all cases we can use the associated trend bar's time range to achieve this)
  const { isTrendsPageFilteredByShift } = useTrendsPageFilterState();
  const titleBarTimeRange = isTrendsPageFilteredByShift
    ? timeRange &&
      trendChartBarRangesForBarstacks.find(
        ({ ge, lt }) => ge <= timeRange.ge && lt >= timeRange.ge
      )
    : timeRange;

  const allProcesses = useAllProcesses();

  return (
    <StyledAnimatedDiv
      className="pnlChartPopupContent"
      style={{
        ...springUpOnOpen,
        width,
        margin: "auto",
        pointerEvents: "none",
        ...(isIe11 ? { margin: "0 auto" } : {}),
      }}
    >
      <PnlChartPopupStyles
        $width={width}
        $isIe11={isIe11}
        $maxHeight={
          isFullPagePopup || animating
            ? fullPageHeight
            : `min(${fullPageHeight}, ${(height ?? 0) - NAV_HEIGHT_PX + "px"})`
        }
        $isFullPagePopup={isFullPagePopup}
        $isHalfWidth={isHubletPage && isDesktopOrLarger}
        $isGridLayoutPage={isGridLayoutPage}
        $isTimelinePage={isTimelinePage}
        $isTrendsPage={isTrendsPage}
        $isHubletPageTabletOrSmaller={isHubletPageTabletOrSmaller}
        $isModalOpen={isModalOpen}
        $isDarkMode={isDarkMode}
        data-component-name={"PnL Popup"}
      >
        {titleBarTimeRange && (
          <PnlPopupTitleBar
            setPnlPopupTimeRange={setPnlPopupTimeRange}
            pnlPopupTimeRange={titleBarTimeRange}
          />
        )}
        <div className="content">
          {timeRange && (
            <>
              <GroupMetricsStyles
                $isPnlPopup={true}
                data-component-name={"PnL Popup Group Metrics"}
              >
                <div className="metricsScrollContainer">
                  <div className="metrics">
                    {metrics
                      .filter(m => m.dataDumpKey)
                      .map(
                        ({
                          displayName,
                          units,
                          dataDumpKey,
                          description,
                          overrideMachemetryIds,
                        }) => {
                          const showTooltip = getShowTooltip({
                            dataDumpKey,
                            ckey,
                            description,
                          });
                          return (
                            <ErrorBoundary
                              key={displayName}
                              id={`PnlPopup_GroupMetric_${dataDumpKey}`}
                            >
                              <GroupMetric
                                isTooLongForDataDump={isTooLongForDataDump}
                                {...{
                                  displayName,
                                  value: calcMetric({
                                    dataDumpKey:
                                      dataDumpKey as keyof DataDumpAggregateType,
                                    isMidgardDDAEnabled,
                                    dataDumpsObjForRange: dataDumpForPopup,
                                    rollupMetric,
                                  }),
                                  units,
                                  dataDumpKey,
                                  description,
                                  showTooltip,
                                  allProcesses,
                                  overrideMachemetryIds,
                                  shouldFetchOverriddenMetric:
                                    overrideMachemetryIds &&
                                    overrideMachemetryIds.length > 0,
                                }}
                                isPnlPopup={true}
                              />
                            </ErrorBoundary>
                          );
                        }
                      )}
                  </div>
                </div>
              </GroupMetricsStyles>
              <PnlSummaryChartContent
                // only use isHubletPage for loading the full-range PnlSummaryChart
                timeRange={pnlPopupTimeRange}
                pnlBreakdownChartCategorySectionsArr={
                  pnlChartSectionsArr || prevPnlChartSections // keep previous so we can still show something as we animate closed
                }
              />
            </>
          )}
          {shouldShowSupervisorContext && timeRange && (
            <ErrorBoundary id="PnlPopup_SupervisorContext">
              <SupervisorContext
                {...getSupervisorCommentDataTimeData(
                  timeRange,
                  timeZoneLocaleString,
                  shouldUseCustomTimeZone,
                  formatZoned
                )}
                pnlPopupTimeRange={timeRange}
                unknownPnlLoss={unknownPnlLoss}
                processId={processIdForSupervisorContext}
              />
            </ErrorBoundary>
          )}
        </div>
      </PnlChartPopupStyles>
    </StyledAnimatedDiv>
  );
}

/** returns true if there's no allowedEmails array in
 * CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING,
 * or if the user's email is in that list
 */
function useIsUserSupervisor(): boolean {
  const ckey = useCustomerKey();
  const { user } = useAuth0();
  const allowedEmails =
    CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING[ckey]?.allowedEmails;
  const isAdminUser = useIsAdmin();

  // the user has access if
  return (
    // - it's a raven user
    isAdminUser ||
    // - there's no allowedEmails list OR
    !allowedEmails ||
    // - the user's email is in allowedEmails
    Boolean(user?.email && allowedEmails.includes(user?.email))
  );
}

const StyledAnimatedDiv = styled(animated.div)`
  @media (max-width: ${BREAKPOINT_DESKTOP_XL}px) {
    max-width: ${CHART_MAX_WIDTH}px;
  }
`;
